import { showDeleteConfirmation, showConfirmation, showYesNo } from '@modules/common';
import { switchMap, tap, map } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ChecklistTemplateId,
  ChecklistTemplateResponse,
  CompactChecklistTemplateResponse,
  ChecklistStepResponse,
  ChecklistId,
} from '@business/entities/checklist';
import { R } from '@core/utils/r';
import { ChecklistTemplateAPI } from '@business/api/checklistTemplate';
import { of } from 'rxjs';

export class ChecklistTemplateBloc extends BaseBloc {
  private readonly checklistRepo = new Repository<CompactChecklistTemplateResponse | ChecklistTemplateResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly selectStep = (checklistId: ChecklistId, stepId: number) =>
    this.checklistRepo.selectItem(checklistId).pipe(
      map(checklist => {
        if (!checklist) return undefined;
        let allSteps = R.flatMap((checklist as ChecklistTemplateResponse).sections || [], section => section.steps);
        // allSteps = R.merge(allSteps, (checklist as ChecklistTemplateResponse).steps);
        allSteps = [...allSteps, ...(checklist as ChecklistTemplateResponse).steps];
        return allSteps.find(s => s.id === stepId);
      }),
    );

  readonly templates$ = this.checklistRepo.items$;
  readonly selectTemplate = this.checklistRepo.selectItem;

  onReset() {
    this.checklistRepo.reset();
  }

  fetchChecklists = (fullResponse = false, allTemplates?: boolean) => {
    return ChecklistTemplateAPI.fetchChecklistTemplates(fullResponse, allTemplates).pipe(
      this.checklistRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  fetchChecklist = (id: ChecklistTemplateId) => {
    return ChecklistTemplateAPI.fetchChecklistTemplate(id).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  createChecklist = (checklist: Partial<ChecklistTemplateResponse>) => {
    return ChecklistTemplateAPI.createChecklistTemplate(checklist).pipe(
      this.checklistRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_checklist'),
      }),
    );
  };

  updateChecklist = (id: ChecklistTemplateId, data: Partial<ChecklistTemplateResponse>) => {
    return ChecklistTemplateAPI.updateChecklistTemplate(id, data).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_checklist'),
      }),
    );
  };

  deleteChecklist = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => ChecklistTemplateAPI.deleteChecklistTemplate(id)),
      this.checklistRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_checklist'),
      }),
    );
  };

  duplicateChecklist = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Duplicate Template', 'Do you really want to duplicate this checklist?', { btnLabel: 'Duplicate' }).pipe(
      switchMap(() => ChecklistTemplateAPI.duplicateChecklistTemplate(id)),
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_checklist'),
      }),
    );
  };

  executeChecklist = (ChecklistTemplateId: ChecklistTemplateId) => {
    return showConfirmation('Execute Checklist', 'Do you really want to execute this checklist?').pipe(
      switchMap(() => ChecklistTemplateAPI.executeChecklistTemplate(ChecklistTemplateId)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('execute_template'),
      }),
    );
  };

  /* Sections */
  createChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, name: string) => {
    return ChecklistTemplateAPI.createChecklistSection(ChecklistTemplateId, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section'),
      }),
    );
  };

  updateChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, id: number, name: string) => {
    return ChecklistTemplateAPI.updateChecklistSection(id, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section'),
      }),
    );
  };

  deleteChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => ChecklistTemplateAPI.deleteChecklistSection(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section'),
      }),
    );
  };

  sortSections = (ChecklistTemplateId: ChecklistTemplateId, sectionIds: number[]) => {
    return ChecklistTemplateAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const checklist = this.checklistRepo.getItem(ChecklistTemplateId)! as ChecklistTemplateResponse;
        const newSections = checklist.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.checklistRepo.updateOne({
          id: ChecklistTemplateId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  /* Steps */

  createChecklistStep = (
    ChecklistTemplateId: ChecklistTemplateId,
    sectionId: number,
    data: Partial<ChecklistStepResponse>,
    parentStepId?: number,
  ) => {
    return ChecklistTemplateAPI.createChecklistStep(ChecklistTemplateId, sectionId, data, parentStepId).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section_step'),
      }),
    );
  };

  updateChecklistStep = (ChecklistTemplateId: ChecklistTemplateId, id: number, data: Partial<ChecklistStepResponse>) => {
    return ChecklistTemplateAPI.updateChecklistStep(id, data).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section_step'),
      }),
    );
  };

  deleteChecklistStep = (ChecklistTemplateId: ChecklistTemplateId, id: number, childCount?: number) => {
    return showDeleteConfirmation(
      'Delete Step',
      `${
        childCount
          ? `You have ${childCount} ${R.plural(childCount, 'Subtask', 'Subtasks')} in this step. These ${R.plural(
              childCount,
              'Subtask',
              'Subtasks',
            )} will also be deleted with this step. Do you really want to remove this Step?`
          : 'Do you really want to remove this Step?'
      }`,
    ).pipe(
      switchMap(() => ChecklistTemplateAPI.deleteChecklistStep(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section_step'),
      }),
    );
  };

  sortSectionSteps = (ChecklistTemplateId: ChecklistTemplateId, sectionId: number, stepIds: number[]) => {
    return ChecklistTemplateAPI.sortSectionSteps(stepIds).pipe(
      tap(() => {
        const checklist = this.checklistRepo.getItem(ChecklistTemplateId)! as ChecklistTemplateResponse;
        const affectedSection = R.find(checklist.sections, s => s.id === sectionId)!;

        let noSectionSteps = false;
        let sectionSteps = [];
        if (affectedSection) {
          sectionSteps = affectedSection.steps;
        } else {
          noSectionSteps = true;
          sectionSteps = R.orderBy(checklist.steps, s => s.menuOrder, ['asc']);
        }

        const newSteps = sectionSteps.map(step => {
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = checklist.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        const newChecklistSteps = R.sortBy(newSteps, s => s.menuOrder);

        this.checklistRepo.updateOne({
          id: ChecklistTemplateId,
          changes: () => (noSectionSteps ? { steps: newChecklistSteps } : { sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_steps_sort'),
      }),
    );
  };

  updateAssignne = (type: string, id: number | string, items: string[], assigneeAdded?: boolean) => {
    if (assigneeAdded) {
      if (type === 'template' || type === 'checklist') {
        return showYesNo('Assign User', `${'Would you like to assign all sections and steps to this user?'}`).pipe(
          switchMap(res => {
            if (res) {
              return showYesNo('Assign User', `${'Would you like to assign all steps in this section to this user?'}`).pipe(
                switchMap(response => {
                  let assignStep = false;
                  if (response) {
                    assignStep = true;
                  }
                  return ChecklistTemplateAPI.updateAssignne(type, id, items, true, assignStep).pipe(
                    this.checklistRepo.ops.upsertOne(item => ({ item })),
                  );
                }),
              );
            } else {
              return ChecklistTemplateAPI.updateAssignne(type, id, items, false, false).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
            }
            return of(null);
          }),
          handleMessage({
            type: requestMessage('update_checklist'),
          }),
        );
      } else if (type === 'section') {
        return showYesNo('Assign User', `${'Would you like to assign all steps in this section to this user?'}`).pipe(
          switchMap(response => {
            let assignStep = false;
            if (response) {
              assignStep = true;
            }
            return ChecklistTemplateAPI.updateAssignne(type, id, items, false, assignStep).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
          }),
          handleMessage({
            type: requestMessage('update_checklist'),
          }),
        );
      } else {
        return ChecklistTemplateAPI.updateAssignne(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
      }
    } else {
      return ChecklistTemplateAPI.updateAssignne(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
    }
  };

  selectUserTeam = (id: string, ids: string[], keepAssignee: boolean) => {
    return ChecklistTemplateAPI.selectUserTeam(id, ids, keepAssignee).pipe(
      switchMap(() => this.fetchChecklist(id)),
      handleMessage({
        type: requestMessage('execute_template'),
      }),
    );
  };
}
